import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams, useNavigate } from 'react-router'

export const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
]

// Feature-flagged languages: only shown when the matching localStorage item is
// set to 'true' (e.g. localStorage.setItem('enableChinese', 'true'), then reload).
const FLAGGED_LANGUAGES = [{ code: 'zh', label: '中文', flag: '🇨🇳', flagKey: 'enableChinese' }]

function getAvailableLanguages() {
  const enabled = FLAGGED_LANGUAGES.filter((lang) => {
    try {
      return localStorage.getItem(lang.flagKey) === 'true'
    } catch {
      return false
    }
  }).map((lang) => ({ code: lang.code, label: lang.label, flag: lang.flag }))
  return [...LANGUAGES, ...enabled]
}

// const LIMITED_LANGUAGES = [
//   { code: 'uk', label: 'Українська', flag: '🇺🇦' },
//   { code: 'en', label: 'English', flag: '🇺🇸' },
//   // { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
//   // { code: 'fr', label: 'Français', flag: '🇫🇷' },
//   // { code: 'es', label: 'Español', flag: '🇪🇸' },
//   // { code: 'da', label: 'Dansk', flag: '🇩🇰' },
//   // { code: 'pl', label: 'Polski', flag: '🇵🇱' },
//   // { code: 'it', label: 'Italiano', flag: '🇮🇹' },
// ]

// function getLanguages(limited: boolean) {
//   return limited ? LIMITED_LANGUAGES : LANGUAGES
// }

export default function LanguageSelector() {
  const { i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [open, setOpen] = useState(false)
  const [languages] = useState(getAvailableLanguages)

  const urlLang = searchParams.get('lang')
  const isWitnessesPage = location.pathname.startsWith('/witnesses')

  useEffect(() => {
    if (!urlLang) return

    const isAllowed = languages.some((l) => l.code === urlLang)

    if (isAllowed) {
      i18n.changeLanguage(urlLang)
    } else {
      const fallback = languages[0].code
      i18n.changeLanguage(fallback)
    }
  }, [location.pathname])

  // close on any click outside (e.g. on the mobile nav menu)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  function changeLang(langCode: string) {
    i18n.changeLanguage(langCode)
    const params = new URLSearchParams(searchParams)
    params.set('lang', langCode)
    navigate(`${location.pathname}?${params.toString()}`, { replace: true })
    setOpen(false)
  }

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0]

  return (
    <div ref={ref} className='relative inline-block text-left'>
      <button
        disabled={isWitnessesPage}
        onClick={() => setOpen(!open)}
        className='flex items-center gap-2 border px-2 py-1 rounded-lg shadow-sm hover:bg-gray-100 my-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
      >
        <span>{currentLang.flag}</span>
      </button>

      {open && (
        <div className='absolute right-0 mt-2 bg-white shadow-xl p-2 z-50'>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLang(lang.code)}
              className='w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer'
            >
              <span>{lang.flag}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
